import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { LaunchOptions, LaunchValue } from "../api";
import { pointersLast, tagWords } from "./campaignBuild";

/**
 * A value picked from a list the server found, or typed when it did not.
 *
 * SHARED, because there are two of these now and they must not drift: a
 * project's launch offers what can be BUILT (branches and tags of the source),
 * a bench offers what is ALREADY BUILT and waiting in a registry. Same shape,
 * same four states, different question - and the second copy of this logic
 * would be the one that forgets the escape entry, or the partial-list note, or
 * that an unknown source must not be given a cost.
 *
 * THE LIST IS NEVER A GATE. It comes from another service, so it can be a
 * minute stale, partial, or missing entirely, and none of those may stop
 * somebody naming what they meant to.
 */

/** The "let me type it" entry. A plain string, not a NUL byte: Benches.tsx
    once carried a NUL sentinel and the file counted as binary, so every grep
    across the UI skipped it without saying so. Braces are outside the value
    pattern the server accepts, and a test on its side keeps them there. */
export const TYPE_IT = "{type-it}";

/** Values by source, in the order the server sent them - first appearance
    wins, nothing is sorted: the configuration decides which source leads. */
export function groupBySource(values: LaunchValue[]): [string, string[]][] {
  const out: [string, string[]][] = [];
  for (const v of values) {
    const row = out.find(([s]) => s === v.source);
    if (row) row[1].push(v.value);
    else out.push([v.source, [v.value]]);
  }
  return out;
}

/**
 * What choosing from this group costs, as a catalogue key - or null for a
 * source nobody has described, which is then shown BY NAME. Inventing "ready
 * now" for an unknown source is the one kind of wrong that cannot be seen.
 *
 * THREE TIERS NOW, and they are the whole reason the groups exist:
 *
 *     bench             already on the rig      seconds
 *     images            built, needs pulling    minutes
 *     branches, tags    has to be built         about a quarter of an hour
 *
 * `bench` came last and is the cheapest: only the rig can know it, because it
 * may hold images nobody published and the registry may hold images it never
 * pulled. Two different sets, both worth offering, and the server puts the
 * free one first.
 */
export function sourceKey(
  source: string,
): "launch.onRig" | "launch.readyNow" | "launch.needsBuild" | null {
  if (source === "bench") return "launch.onRig";
  if (source === "images") return "launch.readyNow";
  if (source === "branches" || source === "tags") return "launch.needsBuild";
  return null;
}

export default function OptionField({
  label,
  options,
  value,
  onChange,
  placeholder,
  required,
  hint,
  mono = true,
}: {
  label: string;
  options?: LaunchOptions;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  required?: boolean;
  /** One line under the field, from the caller: what this particular list IS,
      which is the thing the two callers do not share. */
  hint?: React.ReactNode;
  mono?: boolean;
}) {
  const { t } = useTranslation();
  const [typing, setTyping] = useState(false);
  const values = options?.values ?? [];
  const words = { rig: t("launch.tagRig"), tests: t("launch.tagTests"), alias: t("launch.tagAlias") };
  const listed = values.length > 0 && !typing;
  const box = `mt-1 w-full rounded border border-slate-300 px-2 py-1 ${
    mono ? "font-mono text-xs" : "text-sm"
  } dark:border-slate-700 dark:bg-slate-800`;

  return (
    <label className="block text-sm">
      <span className="text-slate-500">
        {label}
        {required && <span className="ml-1 text-red-600">*</span>}
      </span>

      {listed ? (
        <select
          className={box}
          value={value}
          aria-label={label}
          onChange={(e) => {
            // The last entry is an escape, not a value.
            if (e.target.value === TYPE_IT) {
              setTyping(true);
              return;
            }
            onChange(e.target.value);
          }}
        >
          <option value="">{placeholder ? `— ${placeholder} —` : "—"}</option>
          {groupBySource(values).map(([source, vals]) => (
            <optgroup
              key={source}
              // Name AND cost. `branches` and `tags` cost the same, so a label
              // built from cost alone put two adjacent groups under an
              // identical heading.
              label={(() => {
                const k = sourceKey(source);
                return k ? `${source} — ${t(k)}` : source;
              })()}
            >
              {(source === "bench" ? pointersLast(vals) : vals).map((x) => (
                // The same name from two sources is two different choices.
                <option key={`${source}:${x}`} value={x}>
                  {source === "bench" ? tagWords(x, words) : x}
                </option>
              ))}
            </optgroup>
          ))}
          <option value={TYPE_IT}>{t("launch.typeIt")}</option>
        </select>
      ) : (
        <input
          className={box}
          placeholder={placeholder}
          value={value}
          aria-label={label}
          onChange={(e) => onChange(e.target.value)}
        />
      )}

      {hint && <span className="mt-0.5 block text-xs text-slate-400">{hint}</span>}

      {/* Four combinations of values and note, not three. */}
      {options && (options.note || values.length === 0) && (
        <span className="mt-0.5 block text-xs text-slate-400">
          {options.note
            ? values.length === 0
              ? t("launch.noList", { why: options.note })
              : t("launch.partialList", { why: options.note })
            : t("launch.emptyList")}
        </span>
      )}
    </label>
  );
}
